import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "../css/contact.css";
import WhatsAppFloat from "../components/whatsapp";
import Footer from "../components/footer";

const Contact = () => {
  // State to hold form input values
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiry: "",
    message: "",
  });

  // State to handle form submission status
  const [status, setStatus] = useState("idle"); // 'idle', 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState("");

  // Scroll to the top of the page when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("https://www.lovelycakes.shop/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        // Clear the form
        setFormData({ name: "", email: "", inquiry: "", message: "" });

        // Reset success message after 5 seconds
        setTimeout(() => setStatus("idle"), 5000);
      } else {
        setStatus("error");
        setErrorMessage(data.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setStatus("error");
      setErrorMessage("Failed to connect to the server. Please try again.");
    }
  };

  return (
    <>
      {/* SEO: Contact Page Meta Data */}
      <Helmet>
        <title> Lovely Cakes | Contact Us</title>
        <meta
          name="description"
          content="Get in touch with Lovely Cakes to order bespoke celebration cakes, corporate gifts, or wedding cakes. Visit our bakery at 222 Prince St, Walsall."
        />
        <meta
          name="keywords"
          content="Contact Lovely Cakes, bakery Walsall, order custom cakes Walsall, cake shop Birmingham area, bespoke cakes West Midlands"
        />
      </Helmet>

      <section className="contact-page">
        <div className="contact-header">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Whether you have a question about a bespoke order or simply want to
            say hello, our bakery team is here to assist you.
          </p>
        </div>

        <div className="contact-grid">
          <div className="contact-info-panel">
            <h2>Our Details</h2>
            <p className="info-description">
              Fill out the form or reach out to us directly through the details
              below. We look forward to baking for you.
            </p>

            <address className="info-list" style={{ fontStyle: "normal" }}>
              <div className="info-item">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                {/* SEO: Make phone number a clickable tel link */}
                <a
                  href="tel:01922687284"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  01922 687 284
                </a>
              </div>

              <div className="info-item">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                {/* SEO: Make email a clickable mailto link */}
                <a
                  href="mailto:lovelycakeswalsall@gmail.com"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  lovelycakeswalsall@gmail.com
                </a>
              </div>

              <div className="info-item">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>222 Prince St, Walsall WS2 9JG</span>
              </div>
            </address>

            <div className="hours-section">
              <h3>Opening Hours</h3>
              <div className="hours-grid">
                <span>Monday — Sunday</span> <span>11am - 7pm</span>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <form
              className="contact-form"
              onSubmit={handleSubmit}
              aria-label="Contact Form"
            >
              <div className="form-row">
                <div className="input-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jane@example.com"
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="inquiry">Inquiry Type</label>
                <select
                  id="inquiry"
                  value={formData.inquiry}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select a subject...
                  </option>
                  <option value="bespoke">Bespoke Cake Order</option>
                  <option value="corporate">Corporate Gifting</option>
                  <option value="other">General Inquiry</option>
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you today?"
                  required
                ></textarea>
              </div>

              {status === "success" && (
                <p style={{ color: "green", fontSize: "0.9rem" }} role="alert">
                  Thank you! Your message has been sent successfully.
                </p>
              )}
              {status === "error" && (
                <p style={{ color: "red", fontSize: "0.9rem" }} role="alert">
                  {errorMessage}
                </p>
              )}

              <div className="btn-container">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={status === "loading"}
                  style={{ opacity: status === "loading" ? 0.7 : 1 }}
                >
                  {status === "loading" ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="contact-map-wrapper">
          <iframe
            title="Lovely Cakes Location Map in Walsall"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2424.6400020589954!2d-2.003912822419209!3d52.576121232071905!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4870994dc6a1aed5%3A0xde7ba8dbcae49b98!2sLovely%20Cakes%20Walsall!5e0!3m2!1sen!2suk!4v1779640329751!5m2!1sen!2suk"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>

        {/* <WhatsAppFloat /> */}
      </section>
      <Footer />
    </>
  );
};

export default Contact;
